import BigNumber from 'bignumber.js'
import React, { useCallback, useMemo, useState, useRef } from 'react'
import { Token } from 'vvs-sdk'
import { Button, Modal, Text } from 'vvs-uikit'
import { ModalActions } from 'components/Modal'
import StepIndicator from 'components/StepIndicator'
import ErrorAlert from 'components/ErrorAlert'
import { useTranslation } from 'contexts/Localization'
import { getFullDisplayBalance } from 'utils/formatBalance'
import useToast from 'hooks/useToast'
import {
  useGetBoostedFarms,
  useGetFarmUserInfo as useGetBoostFarmUserInfo,
  useUpdateShares,
} from 'hooks/boost-farm/useFarmBoostContract'
import { useTransactionAdder } from 'state/transactions/hooks'
import { isFarmBoostingSupported, isEcoFarmSupported } from 'utils/isNftStakingSupported'
import {
  getRewarderV2AddressByPid,
  useViewUserInfo as useViewUserInfoInRewarderV2,
} from 'hooks/rewarderV2/useRewarderV2Contract'
import waitPromise from 'utils/waitPromise'

import FarmLpAmountInput from './FarmLpAmountInput'

interface WithdrawModalProps {
  max: BigNumber
  lpPrice: BigNumber
  onConfirm: (amount: string) => Promise<void>
  onDismiss?: () => void
  tokenName?: string
  token: Token
  quoteToken: Token
  pid: number
}

const WithdrawModal: React.FC<WithdrawModalProps> = ({
  onConfirm,
  onDismiss,
  max,
  lpPrice,
  tokenName = '',
  token,
  quoteToken,
  pid,
}) => {
  const [val, setVal] = useState('')
  const { toastSuccess, toastError } = useToast()
  const [running, setRunning] = useState(false)
  const { t } = useTranslation()
  const fullBalance = useMemo(() => {
    return getFullDisplayBalance(max)
  }, [max])
  const { data: boostedFarms, isLoading: isBoostedFarmsLoading } = useGetBoostedFarms()
  const { data: farmBoostUserInfo, isLoading: isGetFarmUserInfoLoading } = useGetBoostFarmUserInfo(pid, {
    enabled: isFarmBoostingSupported(pid), // only supported boosting farm need to call this ABI, [farm.enableBoost = true]
  })
  const { data: viewUserInfoData, isLoading: isViewUserInfoLoading } = useViewUserInfoInRewarderV2(
    getRewarderV2AddressByPid(pid),
    pid,
    {
      enabled: isEcoFarmSupported(pid), // only supported eco boost farm need to call this ABI, [farm.ecoFarm != false / undefined]
    },
  )

  const isBoostedFarm = boostedFarms && boostedFarms.some((farmId) => farmId === pid)
  const [currentStep, setCurrentStep] = useState(1)
  const stepRef = useRef<number>()
  stepRef.current = currentStep
  const [updateShareHasError, setUpdateShareHasError] = useState(false)

  const valNumber = new BigNumber(val)
  const fullBalanceNumber = new BigNumber(fullBalance)

  const addTransaction = useTransactionAdder()

  const { mutateAsync: updateShares, isLoading: isUpdateSharesLoading } = useUpdateShares()

  const handleSelectMax = useCallback(() => {
    setVal(fullBalance)
  }, [fullBalance, setVal])

  const isNftStakingPreventing = useMemo(() => {
    const farmBoostContractBoostingNfts = farmBoostUserInfo?.userInfo && (farmBoostUserInfo?.userInfo[2] as [])
    const rewarderV2BoostingNfts = viewUserInfoData && (viewUserInfoData[2] as [])

    // preventing confirm only when:  1) val === fullBalance. 2) has boosting Nfts
    if (
      parseFloat(val) >= parseFloat(fullBalance) &&
      (farmBoostContractBoostingNfts?.length > 0 || rewarderV2BoostingNfts?.length > 0)
    )
      return true

    return false
  }, [farmBoostUserInfo, fullBalance, val, viewUserInfoData])

  const usdToStake = useMemo(() => new BigNumber(val).times(lpPrice), [val, lpPrice])

  return (
    <Modal
      maxWidth="512px"
      title={currentStep === 1 ? t('Unstake LP Tokens') : t('Update Boost Rewards')}
      onDismiss={onDismiss}
    >
      {isBoostedFarm && (
        <StepIndicator
          running={running}
          steps={[t('Unstake'), t('Update rewards')]}
          currentStep={currentStep}
          stepLabelProps={{ fontSize: '13px' }}
          mb="1.25em"
        />
      )}
      {currentStep === 1 && (
        <FarmLpAmountInput
          onSelectMax={handleSelectMax}
          onChange={(newValue) => setVal(newValue)}
          value={val}
          max={fullBalance}
          symbol={tokenName}
          inputTitle={t('Unstake')}
          token={token}
          quoteToken={quoteToken}
          isWarning={!isGetFarmUserInfoLoading && !isViewUserInfoLoading && isNftStakingPreventing}
          valueInUsdHint={usdToStake}
        />
      )}
      {currentStep === 2 && (
        <>
          <Text>
            {t(
              'Unstaking LP to your boosted farm will affect boosted APR and boosted rewards, please to make sure your boost is most updated.',
            )}
          </Text>
          {updateShareHasError && (
            <ErrorAlert mt="1.5em">
              {t(
                'Your boost is not most updated, this will impact your earnings on the boosted farms. Please click "Confirm" to update your boost.',
              )}
            </ErrorAlert>
          )}
        </>
      )}
      {isNftStakingPreventing && (
        <Text bold fontSize="13px" color="failure">
          {t('Please unstake your NFT(s) before removing all LP Tokens')}
        </Text>
      )}

      <ModalActions>
        <Button variant="secondary" onClick={onDismiss} width="100%" disabled={running || currentStep > 1}>
          {t('Cancel')}
        </Button>
        <Button
          disabled={
            running ||
            !valNumber.isFinite() ||
            valNumber.eq(0) ||
            valNumber.gt(fullBalanceNumber) ||
            isBoostedFarmsLoading ||
            isUpdateSharesLoading ||
            isGetFarmUserInfoLoading ||
            isViewUserInfoLoading ||
            isNftStakingPreventing
          }
          onClick={async () => {
            setRunning(true)
            setUpdateShareHasError(false)
            try {
              if (stepRef.current === 1) {
                await onConfirm(val)
                toastSuccess(t('Unstaked!'), t('Your earnings have also been harvested to your wallet'))

                if (isBoostedFarm) {
                  setCurrentStep(2)
                  await waitPromise(3000)
                }
              }

              if (isBoostedFarm && stepRef.current === 2) {
                const { tx } = await updateShares({ farmId: pid })
                addTransaction(tx, {
                  title: t('Your staked liquidity is updated successfully!'),
                  summary: t('Your boost apr will be optimised and adjusted accordingly'),
                })
                setCurrentStep(3)
                await waitPromise(2000)
              }

              onDismiss()
            } catch (e) {
              console.error(e)
              switch (stepRef.current) {
                case 1:
                  toastError(
                    t('Error'),
                    t(
                      'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
                    ),
                  )
                  break
                case 2:
                  setUpdateShareHasError(true)
                  toastError(
                    t('Error in updating shares'),
                    t('Network is congested or you have rejected the transaction'),
                  )
                  break
                default:
                  toastError(t('Error'), t(e.message))
              }
            } finally {
              setRunning(false)
            }
          }}
          width="100%"
        >
          {running ? t('Confirming...') : t('Confirm')}
        </Button>
      </ModalActions>
    </Modal>
  )
}

export default WithdrawModal
